import React, { useState } from 'react';
import { Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCollaborations, STATUS_CONFIG } from '../context/CollaborationsContext';
import { formatEuro, parseCurrency } from '../utils/helpers';
import BuscadorProductos from './BuscadorProductos';
import { detalleProducto } from '../lib/detalleProducto';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const EXTRA_FIELDS = [
  { key: 'usuario',         label: 'Usuario',             type: 'text',   section: 'Información general' },
  { key: 'pedido',          label: 'Pedido',              type: 'text',   section: 'Información general' },
  { key: 'codigoDescuento', label: 'Código de descuento', type: 'text',   section: 'Información general' },
  { key: 'income',          label: 'Ingresos',            type: 'number', section: 'Económico' },
  { key: 'cost',            label: 'Coste',               type: 'number', section: 'Económico' },
  { key: 'nombreApellidos', label: 'Nombre y apellidos',  type: 'text',   section: 'Datos personales' },
  { key: 'dni',             label: 'DNI',                 type: 'text',   section: 'Datos personales' },
  { key: 'email',           label: 'Email',               type: 'email',  section: 'Datos personales' },
  { key: 'telefono',        label: 'Teléfono',            type: 'tel',    section: 'Datos personales' },
  { key: 'direccion',       label: 'Dirección',           type: 'text',   section: 'Dirección' },
  { key: 'codigoPostal',    label: 'Código postal',       type: 'text',   section: 'Dirección' },
  { key: 'ciudad',          label: 'Ciudad',              type: 'text',   section: 'Dirección' },
  { key: 'provincia',       label: 'Provincia',           type: 'text',   section: 'Dirección' },
  { key: 'plataforma',      label: 'Plataforma',          type: 'select', section: 'Plataforma' },
  { key: 'contenido',       label: 'Contenido',           type: 'text',   section: 'Plataforma' },
];

// El orden de la ficha. "Productos" se renderiza aparte (lleva el buscador, no
// campos), pero vive aquí para que el orden se lea de un vistazo en un sitio.
const SECTIONS = [
  'Información general',
  'Económico',
  'Productos',
  'Datos personales',
  'Dirección',
  'Plataforma',
];

// Mismo tope que en el formulario público.
const MAX_PRODUCTOS = 5;

// Las mismas que ofrece el formulario. Si una colaboración antigua trae otra
// cosa, se añade a la lista para no perderla al abrir la ficha.
const PLATAFORMAS = ['Instagram', 'TikTok', 'YouTube'];

// Lo que se publica en cada plataforma si nadie lo cambia. Es el texto que
// acaba en la cláusula primera del contrato, así que se deja editable: aquí
// solo se autorellena. Mismos valores que en server/lib/colaboraciones.js.
const CONTENIDO_POR_DEFECTO = {
  Instagram: '1 reel + 3 historias',
  TikTok: '1 Tiktok',
  YouTube: '1 vídeo de YouTube',
};

const SECTION_GRIDS = {
  'Información general': [['usuario', 'pedido'], ['codigoDescuento']],
  'Económico':           [['income', 'cost']],
  'Datos personales':    [['nombreApellidos', 'dni'], ['email', 'telefono']],
  'Dirección':           [['direccion'], ['codigoPostal', 'ciudad'], ['provincia']],
  'Plataforma':          [['plataforma'], ['contenido']],
};

function FormField({ field, value, onChange, soloLectura, ayuda }) {
  const isCurrency =
    field.type === 'number' && (field.key === 'income' || field.key === 'cost');
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');

  if (field.type === 'select') {
    const opciones = PLATAFORMAS.includes(value) || !value ? PLATAFORMAS : [...PLATAFORMAS, value];
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <Select value={value || ''} onValueChange={(v) => onChange(field.key, v)}>
          <SelectTrigger>
            <SelectValue placeholder="Elige una plataforma" />
          </SelectTrigger>
          <SelectContent>
            {opciones.map((opcion) => (
              <SelectItem key={opcion} value={opcion}>
                {opcion}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // El coste sale de los productos cuando los hay: editarlo a mano solo serviría
  // para que el siguiente cambio de productos lo pisara.
  if (isCurrency && soloLectura) {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted/40 px-3 py-1 text-sm text-muted-foreground">
          {formatEuro(value)}
        </div>
        {ayuda && <p className="text-xs text-muted-foreground">{ayuda}</p>}
      </div>
    );
  }

  if (isCurrency) {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        {editing ? (
          <Input
            autoFocus
            type="text"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            onBlur={() => {
              onChange(field.key, parseCurrency(raw));
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onChange(field.key, parseCurrency(raw));
                setEditing(false);
              }
              if (e.key === 'Escape') setEditing(false);
            }}
            placeholder="0,00 €"
          />
        ) : (
          <div
            className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-[0_1px_4px_rgba(15,23,42,0.04)] items-center cursor-text"
            onClick={() => {
              const n = Number(value) || 0;
              setRaw(n === 0 ? '' : String(n).replace('.', ','));
              setEditing(true);
            }}
          >
            {formatEuro(value)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>{field.label}</Label>
      <Input
        type={field.type}
        value={value || ''}
        onChange={(e) => onChange(field.key, e.target.value)}
        placeholder={field.label}
      />
    </div>
  );
}

export default function CollabDetailModal({ collab, onClose }) {
  const { updateCollaboration } = useCollaborations();
  const [formData, setFormData] = useState(() => {
    const d = {};
    EXTRA_FIELDS.forEach((f) => {
      d[f.key] = collab[f.key] !== undefined ? collab[f.key] : '';
    });
    if (!d.contenido) d.contenido = CONTENIDO_POR_DEFECTO[d.plataforma] || '';
    return d;
  });
  const [name, setName] = useState(collab.name);
  const [editingName, setEditingName] = useState(false);
  const [productos, setProductos] = useState(() =>
    Array.isArray(collab.productos) ? collab.productos : [],
  );

  function anadirProducto(elegido) {
    setProductos((previo) => {
      if (previo.length >= MAX_PRODUCTOS) return previo;
      const repetido = previo.some(
        (p) => p.productId === elegido.productId && p.opId === elegido.opId,
      );
      return repetido ? previo : [...previo, elegido];
    });
  }

  function quitarProducto(indice) {
    setProductos((previo) => previo.filter((_, i) => i !== indice));
  }

  // Con productos, el coste es su suma y se recalcula solo al añadir o quitar.
  const costeDeProductos = productos.reduce((suma, p) => suma + (Number(p.precio) || 0), 0);
  const costeCalculado = productos.length > 0;
  function handleChange(key, value) {
    setFormData((p) => {
      const siguiente = { ...p, [key]: value };
      // Al cambiar de plataforma, el contenido autorrellenado la sigue. Si se
      // escribió otra cosa a mano, se respeta.
      const sinPersonalizar =
        !p.contenido || Object.values(CONTENIDO_POR_DEFECTO).includes(p.contenido);
      if (key === 'plataforma' && sinPersonalizar) {
        siguiente.contenido = CONTENIDO_POR_DEFECTO[value] || '';
      }
      return siguiente;
    });
  }

  function handleSave() {
    const updates = { ...formData, name };
    if (updates.income !== undefined) updates.income = Number(updates.income) || 0;
    if (updates.cost !== undefined) updates.cost = Number(updates.cost) || 0;
    // Del navegador solo se manda el par de ids: el backend rehace el nombre y
    // el precio desde el catálogo.
    updates.productos = productos.map((p) => ({ productId: p.productId, opId: p.opId }));
    if (costeCalculado) updates.cost = costeDeProductos;
    updateCollaboration(collab.id, updates);
    toast.success('Datos guardados correctamente');
    onClose();
  }

  const cfg = STATUS_CONFIG[collab.status] || STATUS_CONFIG['nueva'];
  const fieldMap = {};
  EXTRA_FIELDS.forEach((f) => {
    fieldMap[f.key] = f;
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              {editingName ? (
                <Input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') setEditingName(false);
                  }}
                  className="text-lg font-semibold"
                />
              ) : (
                <DialogTitle
                  onClick={() => setEditingName(true)}
                  title="Clic para editar"
                  className="cursor-text"
                >
                  {name}
                </DialogTitle>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                  style={{ background: cfg.bg }}
                >
                  {cfg.label}
                </span>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5">
          {SECTIONS.map((section) => {
            // Productos no son campos: son el buscador y la lista de elegidos.
            if (section === 'Productos') {
              return (
              <div key={section} className="space-y-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1.5 border-b">
                  Productos
                </div>

                {productos.length > 0 && (
                  <ul className="space-y-2">
                    {productos.map((p, i) => (
                      <li
                        key={`${p.productId}-${p.opId}-${i}`}
                        className="flex items-center gap-2 rounded-md border bg-card px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">{p.nombre}</p>
                          <p className="break-words text-xs text-muted-foreground">
                            {detalleProducto(p)}
                          </p>
                        </div>
                        <span className="shrink-0 text-sm font-semibold">
                          {p.precio != null ? formatEuro(p.precio) : '—'}
                        </span>
                        <button
                          type="button"
                          onClick={() => quitarProducto(i)}
                          title="Quitar producto"
                          className="shrink-0 p-0.5 text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <BuscadorProductos
                  onElegir={anadirProducto}
                  deshabilitado={productos.length >= MAX_PRODUCTOS}
                  textoDeshabilitado={`Máximo de ${MAX_PRODUCTOS} productos. Quita alguno para añadir otro.`}
                />
              </div>
              );
            }

            const rows = SECTION_GRIDS[section] || [];
            return (
              <div key={section} className="space-y-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1.5 border-b">
                  {section}
                </div>
                {rows.map((rowKeys, i) => (
                  <div
                    key={i}
                    className="grid gap-3"
                    style={{
                      gridTemplateColumns:
                        rowKeys.length === 1 ? '1fr' : `repeat(${rowKeys.length}, 1fr)`,
                    }}
                  >
                    {rowKeys.map((key) => (
                      <FormField
                        key={key}
                        field={fieldMap[key]}
                        value={key === 'cost' && costeCalculado ? costeDeProductos : formData[key]}
                        soloLectura={key === 'cost' && costeCalculado}
                        ayuda={
                          key === 'cost' && costeCalculado
                            ? productos.length === 1
                              ? 'Suma de 1 producto elegido'
                              : `Suma de los ${productos.length} productos elegidos`
                            : undefined
                        }
                        onChange={handleChange}
                      />
                    ))}
                  </div>
                ))}
              </div>
            );
          })}

        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4" />
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
